/**
 * http-client.ts — typed fetch wrapper seam.
 *
 * ALL outbound API calls in apps/web MUST go through this module.
 * Único lugar para headers de auth, manejo de errores, detección offline, retry,
 * y el refresco silencioso del access token ante un 401.
 */
import { config } from "./config.js";

export class HttpError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.body = body;
  }
}

// Ruta de refresco — exenta del interceptor de 401/refresh para evitar recursión.
const REFRESH_PATH = "/auth/refresh";

// Token JWT en memoria (lo setea el store de auth; persiste en localStorage aparte).
let authToken: string | null = null;
export function setAuthToken(token: string | null): void {
  authToken = token;
}

// Handler global para 401 (sesión expirada/inválida). Lo registra el auth context
// para cerrar sesión y redirigir al login. Es el FALLBACK cuando el refresh falla.
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null): void {
  onUnauthorized = fn;
}

// Handler de refresco silencioso. Lo registra el auth context: lee el refresh token,
// llama al API, actualiza el estado y re-inyecta el nuevo access token. Devuelve
// true si renovó con éxito, false si no (→ se cae al fallback de logout).
let onRefresh: (() => Promise<boolean>) | null = null;
export function setRefreshHandler(fn: (() => Promise<boolean>) | null): void {
  onRefresh = fn;
}

/** Si un request autenticado devuelve 401, dispara el handler UNA vez por ráfaga. */
function notifyIfUnauthorized(status: number): void {
  if (status === 401 && authToken !== null) {
    authToken = null; // dedupe: evita re-disparar con requests en paralelo
    onUnauthorized?.();
  }
}

function headers(extra?: Record<string, string>): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json", ...extra };
  if (authToken) h["Authorization"] = `Bearer ${authToken}`;
  return h;
}

type HttpMethod = "GET" | "POST" | "PATCH" | "PUT" | "DELETE";

interface RequestOptions {
  /** Marca un request ya reintentado tras un refresh, para no entrar en bucle. */
  retried?: boolean;
}

/** ¿Esta request es elegible para el flujo de refresco silencioso ante un 401? */
function canRefresh(path: string, opts: RequestOptions): boolean {
  return (
    !opts.retried && // todavía no se reintentó
    authToken !== null && // había una sesión activa (no es un login anónimo)
    onRefresh !== null && // hay un handler de refresco registrado
    !path.startsWith(REFRESH_PATH) // la propia llamada de refresco NO se intercepta
  );
}

/**
 * Núcleo único de todos los verbos.
 *
 * Flujo de 401 en request autenticado:
 *   1. Si es elegible (no reintentado, hay sesión + handler, no es /auth/refresh),
 *      se intenta refrescar el access token.
 *   2. Si el refresh devuelve true → se reintenta el request EXACTAMENTE una vez
 *      con el token nuevo (retried=true).
 *   3. Si el refresh devuelve false (o el reintento vuelve a dar 401) → se cae al
 *      fallback: notifyIfUnauthorized (logout + toast) y se lanza el error.
 */
async function request<T>(
  method: HttpMethod,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const hasBody = body !== undefined && method !== "GET" && method !== "DELETE";
  const res = await fetch(`${config.apiUrl}${path}`, {
    method,
    headers: hasBody ? headers({ "Content-Type": "application/json" }) : headers(),
    body: hasBody ? JSON.stringify(body) : undefined,
  });

  // Refresco silencioso antes de rendirse ante un 401.
  if (res.status === 401 && canRefresh(path, opts)) {
    const refreshed = await onRefresh!();
    if (refreshed) {
      // Reintento único con el token ya renovado.
      return request<T>(method, path, body, { retried: true });
    }
    // El refresh falló → cae al manejo normal de 401 (logout).
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    notifyIfUnauthorized(res.status);
    const msg =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `${method} ${path} → ${res.status}`;
    throw new HttpError(res.status, msg, data);
  }
  return data as T;
}

export function httpGet<T>(path: string): Promise<T> {
  return request<T>("GET", path);
}

export function httpPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("POST", path, body);
}

export function httpPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PATCH", path, body);
}

export function httpPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>("PUT", path, body);
}

/** DELETE — éxito sin cuerpo (204). No devuelve nada. */
export async function httpDelete(path: string): Promise<void> {
  await request<unknown>("DELETE", path);
}
